/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\StorageSettingsTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import React, { useState } from 'react';
import { api } from '../../../../lib/api';
import { HardDrive, CheckCircle, AlertCircle, Save } from 'lucide-react';

export default function StorageSettingsTab() {
    const [config, setConfig] = useState({
        type: 'local',
        region: 'us-east-1',
        endpoint: '',
        bucket: '',
        accessKeyId: '',
        secretAccessKey: '',
        host: '',
        user: '',
        password: '',
        secure: true
    });
    const [isTesting, setIsTesting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mesage, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));
    };

    const handleTestConnection = async () => {
        setIsTesting(true);
        setMessage(null);
        try {
            await api.post('/storage/config/test', config);
            setMessage({ type: 'success', text: 'Connection successful!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Connection failed' });
        } finally {
            setIsTesting(false);
        }
    };

    const handleSave = async () => {
        // Placeholder for saving config - currently no endpoint to save permanently in DB in this implementation plan
        // But we can simulate it or just use it for runtime for now if we had a persistent store
        // For now, let's just show a message that this is a placeholder or actually save if we add the endpoint.
        // The current backend doesn't have a specific "Save Config" endpoint, only "Test".
        // We would need to implement SystemVars or similar to store this.
        // For this task scope, we focused on the mechanism. I will note this limitation.
        setIsSaving(true);
        setTimeout(() => {
            setIsSaving(false);
            setMessage({ type: 'success', text: 'Configuration saved (Placeholder)' });
        }, 1000);
    };

    return (
        <div className="space-y-6">
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
                <h3 className="text-lg font-medium flex items-center mb-6">
                    <HardDrive className="mr-2 h-5 w-5 text-primary" />
                    External Storage Configuration
                </h3>

                {mesage && (
                    <div className={`mb-4 p-4 rounded-md flex items-center ${mesage.type === 'success' ? 'bg-green-500/15 text-green-600' : 'bg-destructive/15 text-destructive'}`}>
                        {mesage.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2" /> : <AlertCircle className="h-5 w-5 mr-2" />}
                        {mesage.text}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Storage Type</label>
                        <select
                            name="type"
                            value={config.type}
                            onChange={handleChange}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                            <option value="local">Local Filesystem</option>
                            <option value="s3">S3 Compatible (AWS, MinIO, DigitalOcean)</option>
                            <option value="ftp">FTP / SFTP</option>
                        </select>
                    </div>

                    {config.type === 's3' && (
                        <>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Region</label>
                                <input name="region" value={config.region} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Bucket / Container</label>
                                <input name="bucket" value={config.bucket} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Endpoint (Optional)</label>
                                <input name="endpoint" value={config.endpoint} onChange={handleChange} placeholder="https://s3.amazonaws.com" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Access Key ID</label>
                                <input name="accessKeyId" value={config.accessKeyId} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Secret Access Key</label>
                                <input name="secretAccessKey" type="password" value={config.secretAccessKey} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                        </>
                    )}

                    {config.type === 'ftp' && (
                        <>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Host</label>
                                <input name="host" value={config.host} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">User</label>
                                <input name="user" value={config.user} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Password</label>
                                <input name="password" type="password" value={config.password} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                            </div>
                            <div className="flex items-center space-x-2 pt-8">
                                <input type="checkbox" name="secure" checked={config.secure} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                                <label className="text-sm font-medium">Use Secure Connection (FTPS/TLS)</label>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center space-x-4 mt-8">
                    <button
                        onClick={handleTestConnection}
                        disabled={isTesting}
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 py-2 px-4"
                    >
                        {isTesting ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-primary text-primary-foreground hover:bg-primary/90 h-10 py-2 px-4 shadow-sm"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
        </div>
    );
}
